import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { pipelineService } from '../services/pipeline/pipeline.service';
import type {
    DataFlowMappingRequest,
    UpdateDataFlowRequest,
    UpdateDataFlowResponse
} from '../services/pipeline/pipeline.types';

/**
 * Update Data Flow Mutation
 */
export const useUpdateDataFlowMutation = (
    options?: UseMutationOptions<
        UpdateDataFlowResponse,
        Error,
        { dataFlowId: string; payload: UpdateDataFlowRequest }
    >
) => {
    return useMutation<
        UpdateDataFlowResponse,
        Error,
        { dataFlowId: string; payload: UpdateDataFlowRequest }
    >({
        mutationFn: ({ dataFlowId, payload }) =>
            pipelineService.updateDataFlow(dataFlowId, payload),
        ...options,
    });
};

/**
 * Run Immediate Job Mutation
 */
export const useRunImmediateJobMutation = (
    options?: UseMutationOptions<
        unknown,
        Error,
        string
    >
) => {
    const queryClient = useQueryClient();
    return useMutation<
        unknown,
        Error,
        string
    >({
        mutationFn: (sessionId: string) =>
            pipelineService.runImmediateJob(sessionId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['pipeline-list'] })
        },
        ...options,
    });
};

/**
 * Run Job Mutation
 */
export const useRunJobMutation = (
    options?: UseMutationOptions<
        unknown,
        Error,
        string
    >
) => {
    return useMutation<
        unknown,
        Error,
        string
    >({
        mutationFn: (sessionId: string) =>
            pipelineService.runJob(sessionId),
        ...options,
    });
};

/**
 * Kill Job Mutation
 */
export const useKillJobMutation = (
    options?: UseMutationOptions<
        unknown,
        Error,
        string
    >
) => {
    const queryClient = useQueryClient();

    return useMutation<
        unknown,
        Error,
        string
    >({
        mutationFn: (sessionId: string) =>
            pipelineService.killJob(sessionId),
        onSuccess: () => {
            setTimeout(() => {
                void queryClient.invalidateQueries({ queryKey: ['pipeline-list'] })
            }, 2000);
        },
        ...options,
    });
};

/**
 * Data Flow Mapping Mutation
 */
export const useDataFlowMappingMutation = (
    options?: UseMutationOptions<
        unknown,
        Error,
        { dataFlowId: string; payload: DataFlowMappingRequest }
    >
) => {
    return useMutation<
        unknown,
        Error,
        { dataFlowId: string; payload: DataFlowMappingRequest }
    >({
        mutationFn: ({ dataFlowId, payload }) =>
            pipelineService.dataFlowMapping(dataFlowId, payload),
        ...options,
    });
};